import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  Shield, Users, Key, History, Search, ChevronRight, Check, X,
  RefreshCw, Loader2, UserCog, Lock, Unlock, Eye, EyeOff,
  AlertTriangle, Clock, User, Building, Filter, Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoleManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  // Data states
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [permissions, setPermissions] = useState({});
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [roleChangeReason, setRoleChangeReason] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Expanded permission view
  const [expandedRole, setExpandedRole] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadRoles(),
        loadUsers(),
        loadAuditLogs(),
        loadStats(),
        loadPermissions()
      ]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load RBAC data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await axios.get(`${API}/rbac/roles`, { headers: getAuthHeader() });
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const params = roleFilter !== 'all' ? { role: roleFilter } : {};
      const response = await axios.get(`${API}/rbac/users`, { 
        headers: getAuthHeader(),
        params 
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/rbac/audit-logs`, { 
        headers: getAuthHeader(),
        params: { limit: 50 }
      });
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/rbac/stats`, { headers: getAuthHeader() });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await axios.get(`${API}/rbac/permissions`, { headers: getAuthHeader() });
      setPermissions(response.data.grouped || {});
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedUser || !newRole) return;
    
    setUpdating(true);
    try {
      await axios.put(
        `${API}/rbac/users/${selectedUser.id}/role`,
        {
          user_id: selectedUser.id,
          new_role: newRole,
          reason: roleChangeReason
        },
        { headers: getAuthHeader() }
      );
      
      toast({
        title: "Success",
        description: `User role updated to ${newRole}`
      });
      
      setShowRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      setRoleChangeReason('');
      loadUsers();
      loadAuditLogs();
      loadStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update role",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || 
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      safety_manager: 'bg-purple-100 text-purple-800 border-purple-200',
      auditor: 'bg-blue-100 text-blue-800 border-blue-200',
      supervisor: 'bg-green-100 text-green-800 border-green-200',
      drone_operator: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      worker: 'bg-gray-100 text-gray-800 border-gray-200',
      client: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: <Shield className="w-4 h-4" />,
      safety_manager: <UserCog className="w-4 h-4" />,
      auditor: <Eye className="w-4 h-4" />,
      supervisor: <Users className="w-4 h-4" />,
      drone_operator: <Eye className="w-4 h-4" />,
      worker: <User className="w-4 h-4" />,
      client: <Building className="w-4 h-4" />
    };
    return icons[role] || <User className="w-4 h-4" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Role Management"
        subtitle="Manage user roles, permissions, and access control"
        icon={Shield}
        variant="red"
        onRefresh={loadData}
        loading={loading}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                  <p className="text-sm text-gray-500">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_roles}</p>
                  <p className="text-sm text-gray-500">Roles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Key className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_permissions}</p>
                  <p className="text-sm text-gray-500">Permissions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <History className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_audit_logs}</p>
                  <p className="text-sm text-gray-500">Audit Logs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles">
            <Shield className="w-4 h-4 mr-2" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="permissions">
            <Key className="w-4 h-4 mr-2" />
            Permissions
          </TabsTrigger>
          <TabsTrigger value="audit">
            <History className="w-4 h-4 mr-2" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>View and manage user roles</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); loadUsers(); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role.role} value={role.role}>
                          {role.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                        {user.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium">{user.username || 'No username'}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={`${getRoleBadgeColor(user.role)} flex items-center gap-1`}>
                        {getRoleIcon(user.role)}
                        {user.role?.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {user.permission_count} permissions
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user);
                          setNewRole(user.role);
                          setShowRoleModal(true);
                        }}
                      >
                        <UserCog className="w-4 h-4 mr-1" />
                        Change Role
                      </Button>
                    </div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No users found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roles.map(role => (
              <Card key={role.role} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getRoleBadgeColor(role.role)}`}>
                        {getRoleIcon(role.role)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{role.display_name}</CardTitle>
                        <CardDescription>{role.description}</CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline">{role.user_count} users</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">
                      {role.permission_count} permissions
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setExpandedRole(expandedRole === role.role ? null : role.role)}
                    >
                      {expandedRole === role.role ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Hide
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </>
                      )}
                    </Button>
                  </div>
                  {expandedRole === role.role && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg max-h-60 overflow-y-auto">
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.map(perm => (
                          <Badge key={perm} variant="secondary" className="text-xs">
                            {perm}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>All Permissions</CardTitle>
              <CardDescription>Complete list of system permissions grouped by category</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(permissions).map(([category, perms]) => (
                  <div key={category}>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 capitalize">
                      <Key className="w-5 h-5 text-purple-500" />
                      {category.replace('_', ' ')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {perms.map(perm => (
                        <div
                          key={perm.key}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                        >
                          <Lock className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium">{perm.description}</p>
                            <p className="text-xs text-gray-500">{perm.key}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Audit Log</CardTitle>
                  <CardDescription>Track all role and permission changes</CardDescription>
                </div>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {auditLogs.map(log => (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <History className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{log.action}</span>
                        <Badge variant="outline">{log.resource_type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        {log.details?.old_role && (
                          <>
                            Role changed from <Badge className={getRoleBadgeColor(log.details.old_role)}>{log.details.old_role}</Badge>
                            {' to '}
                            <Badge className={getRoleBadgeColor(log.details.new_role)}>{log.details.new_role}</Badge>
                          </>
                        )}
                        {log.details?.reason && (
                          <span className="ml-2 text-gray-500">- {log.details.reason}</span>
                        )}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {log.actor_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {auditLogs.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No audit logs found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRoleModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Change User Role
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>User</Label>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mt-1">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                    {selectedUser.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium">{selectedUser.username}</p>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Current Role</Label>
                <div className="mt-1">
                  <Badge className={`${getRoleBadgeColor(selectedUser.role)} text-sm`}>
                    {selectedUser.role?.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label>New Role</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select new role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.role} value={role.role}>
                        <div className="flex items-center gap-2">
                          {getRoleIcon(role.role)}
                          {role.display_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Reason for Change (optional)</Label>
                <Input
                  placeholder="e.g., Promotion, Department transfer"
                  value={roleChangeReason}
                  onChange={(e) => setRoleChangeReason(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              {newRole && newRole !== selectedUser.role && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Permission Changes</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        This will change the user&apos;s access level. The action will be logged.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowRoleModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRoleChange}
                disabled={updating || newRole === selectedUser.role}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {updating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Update Role
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
